import React from 'react';
import MuiAutocomplete, {
	AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import { lighten } from '@mui/system';

// Monorepo
import { MixedFieldContent } from '@constituenthub/common';

// Components
import { SelectOption } from './AutoComplete';

type AutoCompleteSelectProps = {
	label?: string;
	required?: boolean;
	disabled?: boolean;
	error?: boolean;
	readOnly?: boolean;
	mixedValue?: boolean;
	autoOpen?: boolean;
	emptyOption?: boolean;
	renderOption?: (
		props: React.HTMLAttributes<HTMLLIElement>,
		option: SelectOption,
		state: AutocompleteRenderOptionState
	) => React.ReactNode;
	delay?: number;
	value?: string | number | boolean | null | undefined;
	onChange: (value: string | number | boolean | null) => void;
	options: SelectOption[];
};

export const AutoCompleteSelect = (props: AutoCompleteSelectProps) => {
	const {
		label,
		required = false,
		disabled = false,
		error = false,
		readOnly = false,
		mixedValue = false,
		autoOpen = false,
		value,
		onChange,
		options,
		renderOption,
	} = props;
	const theme = useTheme();

	const onOptionSelected = (item: SelectOption | null | undefined) => {
		const option = options.find((x) => x.value === item?.value);
		onChange(option?.value || null);
	};

	return (
		<MuiAutocomplete
			isOptionEqualToValue={(option, value) =>
				option.value === value.value
			}
			autoHighlight
			freeSolo={false}
			autoComplete={false}
			disabled={disabled || readOnly}
			autoSelect={true}
			clearOnEscape
			openOnFocus={autoOpen && !value}
			value={options.find((x) => x.value === value) || null}
			onChange={(_event, option: any) => onOptionSelected(option)}
			options={
				options.length ? options : [{ label: 'loading', value: null }]
			}
			renderOption={renderOption}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					placeholder={mixedValue ? MixedFieldContent : label}
					autoComplete="off"
					variant="outlined"
					size="small"
					margin="dense"
					fullWidth
					required={required}
					disabled={disabled}
					error={error}
					onFocus={(event) => event.target.select()}
					InputLabelProps={{ shrink: true }}
					InputProps={{
						...params.InputProps,
						autoComplete: 'off',
						required: required,
						disabled: disabled,
						readOnly: readOnly,
						error: error,
						sx: {
							backgroundColor: mixedValue
								? lighten(theme.palette.primary.main, 0.9)
								: 'initial',
						},
					}}
				/>
			)}
		/>
	);
};
